// src/contexts/AuthContext-401-Fix.jsx
import React, { createContext, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Create the auth context
const AuthContext = createContext(null);

// Base API URL with better fallback handling
const API_URL = process.env.REACT_APP_API_URL+'/auth' || 
                (window.location.hostname === 'localhost' ? 
                 'http://localhost:8000/auth' : 
                 `${window.location.protocol}//${window.location.host}/auth`);
console.log('API URL:', API_URL);
// Create axios instance with authorization header
const basicAxios = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokenRefreshInProgress, setTokenRefreshInProgress] = useState(false);
  const navigate = useNavigate();

  // Helper to save auth data - defined early as it's used in multiple places
  const saveUserData = useCallback((userData, tokens) => {
    if (tokens) {
      localStorage.setItem('access_token', tokens.access_token);
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  }, []);

  // Helper to handle logout (both local and API)
  const handleLogout = useCallback((shouldRedirect = true) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
    
    if (shouldRedirect) {
      navigate('/login');
    }
  }, [navigate]);

  // Function to get the current access token
  const getAccessToken = useCallback(() => {
    return localStorage.getItem('access_token');
  }, []);

  // Create authenticated axios instance with interceptors
  const authAxios = useMemo(() => {
    const instance = axios.create({
      baseURL: API_URL,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    // Add authorization header interceptor
    instance.interceptors.request.use(
      (config) => {
        const token = getAccessToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for token refresh
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry && !tokenRefreshInProgress) {
          originalRequest._retry = true;
          setTokenRefreshInProgress(true);
          
          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            // Use the correct endpoint from your Django urls.py
            const { data } = await basicAxios.post(`/refresh-token/`, {
              refresh_token: refreshToken
            });
            
            if (data.status && data.access_token) {
              localStorage.setItem('access_token', data.access_token);
              // Set the token for future requests
              originalRequest.headers['Authorization'] = `Bearer ${data.access_token}`;
              setTokenRefreshInProgress(false);
              
              // Return the modified request
              return axios(originalRequest);
            } else {
              throw new Error('Token refresh failed');
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // If refresh fails, logout
            handleLogout();
            setTokenRefreshInProgress(false);
            return Promise.reject(refreshError);
          }
        }
        
        // For other errors, just reject the promise
        return Promise.reject(error);
      }
    );

    return instance;
  }, [getAccessToken, handleLogout, tokenRefreshInProgress]);

  // Get user profile (authenticated)
  const getUserProfile = useCallback(async () => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await authAxios.get(`/profile/`);
      
      if (response.data.status && response.data.user) {
        saveUserData(response.data.user);
        
        return { 
          success: true, 
          user: response.data.user 
        };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Failed to get user profile' 
      };
    } catch (error) {
      console.error('Get user profile error:', error);
      
      // Check if the error is a 401 and not already in token refresh
      if (error.response?.status === 401 && !tokenRefreshInProgress) {
        handleLogout(false);
      }
      
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to get user profile' 
      };
    }
  }, [authAxios, saveUserData, tokenRefreshInProgress, handleLogout]);

  // Initialize auth state from localStorage on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const storedUser = localStorage.getItem('user');
      const accessToken = localStorage.getItem('access_token');
      
      if (storedUser && accessToken) {
        try {
          // Parse the stored user data
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Only try to validate token if we have both user and token
          if (parsedUser && accessToken) {
            // Optionally validate the token with a lightweight API call
            const userProfile = await getUserProfile();
            if (!userProfile.success) {
              console.warn('Token validation failed, logging out');
              throw new Error('Invalid token or user profile');
            }
          }
        } catch (error) {
          console.error('Auth initialization error:', error);
          // Clear auth data if error
          handleLogout(false); // Don't redirect
        }
      }
      
      setLoading(false);
    };

    initializeAuth();
  }, [getUserProfile, handleLogout]);

  // Google login
  const googleLogin = useCallback(async (idToken) => {
    try {
      // Use the correct endpoint from your urls.py
      console.log(1);
      const response = await basicAxios.post(`/google-login/`, { id_token: idToken }, {withCredentials: true});
      console.log('2 Google login response:', response);
      if (response.data.status) {
        saveUserData(response.data.user, {
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token
        });
        return { success: true };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Google login failed' 
      };
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'An error occurred during Google login' 
      };
    }
  }, [saveUserData]);

  // Manual login
  const login = useCallback(async (email, password) => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await basicAxios.post(`/login/`, { email, password });
      
      if (response.data.status) {
        saveUserData(response.data.user, {
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token
        });
        return { success: true };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Invalid email or password' 
      };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  }, [saveUserData]);

  // Registration
  const register = useCallback(async (name, email, password) => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await basicAxios.post(`/register/`, { 
        name, 
        email, 
        password 
      });
      
      if (response.data.status) {
        saveUserData(response.data.user, {
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token
        });
        return { success: true };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Registration failed' 
      };
    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  }, [saveUserData]);

  // Forgot password
  const forgotPassword = useCallback(async (email) => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await basicAxios.post(`/forgot-password/`, { email });
      
      return { 
        success: response.data.status, 
        token: response.data.token,
        message: response.data.message,
        error: response.data.error
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to send reset email' 
      };
    }
  }, []);

  // Verify OTP
  const verifyOTP = useCallback(async (token, otp) => {
    try {
      // Use the correct endpoint from your urls.py with PUT method
      const response = await basicAxios.put(`/forgot-password/`, { token, otp });
      
      return { 
        success: response.data.status, 
        message: response.data.message,
        error: response.data.error
      };
    } catch (error) {
      console.error('OTP verification error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'OTP verification failed' 
      };
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (token, newPassword) => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await basicAxios.post(`/reset-password/`, { 
        token, 
        new_password: newPassword 
      });
      
      return { 
        success: response.data.status, 
        message: response.data.message,
        error: response.data.error
      };
    } catch (error) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Password reset failed' 
      };
    }
  }, []);

  // Change password (authenticated)
  const changePassword = useCallback(async (oldPassword, newPassword) => {
    try {
      // Use the correct endpoint from your urls.py
      const response = await authAxios.post(`/change-password/`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      
      if (response.data.status) {
        // Update tokens if provided
        if (response.data.access_token && response.data.refresh_token) {
          saveUserData(null, {
            access_token: response.data.access_token,
            refresh_token: response.data.refresh_token
          });
        }
        
        return { 
          success: true, 
          message: response.data.message 
        };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Failed to change password' 
      };
    } catch (error) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to change password' 
      };
    }
  }, [authAxios, saveUserData]);

  // Edit profile (authenticated)
  const editProfile = useCallback(async (changeType, value) => {
    try {
      const data = { change: changeType };
      
      if (changeType === 'name') {
        data.new_name = value;
      } else if (changeType === 'email') {
        data.new_email = value;
      } else {
        return { 
          success: false, 
          error: 'Invalid change type' 
        };
      }
      
      // Use the correct endpoint from your urls.py
      const response = await authAxios.post(`/edit-profile/`, data);
      
      if (response.data.status) {
        // Update user in state and localStorage
        if (user) {
          const updatedUser = { ...user };
          
          if (changeType === 'name') {
            updatedUser.first_name = value;
          } else if (changeType === 'email') {
            updatedUser.email = value;
            updatedUser.username = value;
          }
          
          saveUserData(updatedUser);
        }
        
        return { 
          success: true, 
          message: response.data.message 
        };
      }
      
      return { 
        success: false, 
        error: response.data.error || 'Failed to update profile' 
      };
    } catch (error) {
      console.error('Edit profile error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update profile' 
      };
    }
  }, [authAxios, user, saveUserData]);

  // Logout
  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        // Use the correct endpoint from your urls.py
        await authAxios.post(`/logout/`, { 
          refresh_token: refreshToken 
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear regardless of API result
      handleLogout();
    }
  }, [authAxios, handleLogout]);

  // Create context value with memoization to prevent unnecessary rerenders
  const contextValue = useMemo(() => ({
    user,
    loading,
    login,
    googleLogin,
    register,
    logout,
    forgotPassword,
    verifyOTP,
    resetPassword,
    changePassword,
    editProfile,
    getUserProfile,
    isAuthenticated: !!user,
    authAxios // Expose the authenticated axios instance for other components
  }), [
    user, 
    loading, 
    login, 
    googleLogin, 
    register, 
    logout, 
    forgotPassword, 
    verifyOTP, 
    resetPassword, 
    changePassword, 
    editProfile, 
    getUserProfile,
    authAxios
  ]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};